import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useUI } from '../../contexts/UIContext';
import { useUserProfile } from '../../hooks/useUserProfile';
import {
  BellIcon,
  BellSlashIcon,
  ClockIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { apiRequest } from '../../utils/apiUtils';

const UserPreferences = () => {
  const { showNotification } = useUI();
  const { profile, loading: profileLoading } = useUserProfile();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [preferences, setPreferences] = useState({
    // Main email frequency control
    email_frequency: 'immediate', // 'immediate', 'weekly_digest', 'disabled'

    // Advanced: Severity filter (only applies to immediate)
    minimum_severity: 'medium', // 'low', 'medium', 'high'

    // Advanced: Specific event types
    receive_validation_failures: true,
    receive_anomalies: true,
    receive_custom_metric_breaches: true,
    receive_schema_changes: true,
    receive_automation_failures: true,
  });

  useEffect(() => {
    let isMounted = true;

    const fetchPreferences = async () => {
      if (isMounted) {
        await loadPreferences();
      }
    };

    fetchPreferences();

    return () => {
      isMounted = false;
    };
  }, []);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      const data = await apiRequest('/user/notification-preferences', {
        method: 'GET'
      });

      if (data.success && data.data) {
        setPreferences(prev => ({
          ...prev,
          ...data.data
        }));

        // Show advanced if user has customized settings
        const hasCustomization =
          data.data.minimum_severity !== 'medium' ||
          !data.data.receive_validation_failures ||
          !data.data.receive_anomalies ||
          !data.data.receive_schema_changes;

        if (hasCustomization) {
          setShowAdvanced(true);
        }
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
      showNotification('Failed to load preferences', 'error');
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    try {
      setSaving(true);
      const data = await apiRequest('/user/notification-preferences', {
        method: 'POST',
        data: preferences
      });

      if (data.success) {
        showNotification('Preferences saved successfully', 'success');
      } else {
        showNotification('Failed to save preferences', 'error');
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      showNotification('Failed to save preferences', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleFrequencyChange = (frequency) => {
    setPreferences(prev => ({
      ...prev,
      email_frequency: frequency
    }));
  };

  if (profileLoading || loading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const FrequencyCard = ({ value, icon: Icon, title, description, badge, recommended }) => {
    const isSelected = preferences.email_frequency === value;

    return (
      <button
        type="button"
        onClick={() => handleFrequencyChange(value)}
        className={`
          relative w-full text-left p-5 rounded-lg border-2 transition-all
          ${isSelected
            ? 'border-primary-500 bg-primary-50 shadow-md'
            : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
          }
        `}
      >
        <div className="flex items-start">
          <div className={`
            flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center
            ${isSelected ? 'bg-primary-500' : 'bg-gray-100'}
          `}>
            <Icon className={`h-5 w-5 ${isSelected ? 'text-white' : 'text-gray-600'}`} />
          </div>

          <div className="ml-4 flex-1">
            <div className="flex items-center gap-2">
              <h4 className={`text-base font-semibold ${isSelected ? 'text-primary-900' : 'text-gray-900'}`}>
                {title}
              </h4>
              {recommended && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                  Recommended
                </span>
              )}
              {badge && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                  {badge}
                </span>
              )}
            </div>
            <p className={`mt-1 text-sm ${isSelected ? 'text-primary-700' : 'text-gray-600'}`}>
              {description}
            </p>
          </div>

          {isSelected && (
            <div className="ml-4 flex-shrink-0">
              <div className="w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center">
                <CheckIcon className="h-4 w-4 text-white" />
              </div>
            </div>
          )}
        </div>
      </button>
    );
  };

  return (
    <div className="max-w-3xl space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-2xl font-bold text-gray-900">Personal Notification Preferences</h3>
        <p className="mt-1 text-sm text-gray-600">
          Control <strong>how often</strong> you receive notifications about data quality issues
        </p>
      </div>

      {/* System Hierarchy Info Callout */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <InformationCircleIcon className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div className="ml-3">
            <h4 className="text-sm font-medium text-blue-900">How Sparvi Notifications Work</h4>
            <p className="mt-1 text-sm text-blue-700">
              Sparvi has a multi-layered notification system:
            </p>
            <ul className="mt-2 text-sm text-blue-700 space-y-1">
              <li>
                <strong>Organization Settings</strong> control <em>what triggers</em> notifications and <em>where</em> they're sent.{' '}
                <Link to="/admin/notifications" className="inline-flex items-center font-medium text-blue-800 hover:text-blue-900 underline">
                  View organization settings
                  <ArrowTopRightOnSquareIcon className="h-3 w-3 ml-1" />
                </Link>
              </li>
              <li>
                <strong>Your personal preferences</strong> (this page) control <em>how often</em> you receive notifications.
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Main Frequency Selection */}
      <div className="space-y-3">
        <FrequencyCard
          value="immediate"
          icon={BellIcon}
          title="Real-time Alerts"
          description="Get notified right away when data quality issues are detected. Best for critical data pipelines."
          badge="Active monitoring"
        />

        <FrequencyCard
          value="weekly_digest"
          icon={ClockIcon}
          title="Weekly Digest"
          description="Receive a summary email every Monday at 8am with all issues from the past week. Less noise, stay informed."
          recommended
        />

        <FrequencyCard
          value="disabled"
          icon={BellSlashIcon}
          title="No Email Notifications"
          description="Turn off all email alerts. You can still view issues in the dashboard."
        />
      </div>

      {/* Stakeholder Group Notifications Callout */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div className="flex">
          <InformationCircleIcon className="h-5 w-5 text-purple-500 flex-shrink-0 mt-0.5" />
          <div className="ml-3">
            <h4 className="text-sm font-medium text-purple-900">Stakeholder Group Notifications</h4>
            <p className="mt-1 text-sm text-purple-700">
              If you're a member of a stakeholder group, you'll receive notifications based on the group's rules when data objects your team consumes are affected.{' '}
              <Link to="/admin/stakeholder-groups" className="inline-flex items-center font-medium text-purple-800 hover:text-purple-900 underline">
                View stakeholder groups
                <ArrowTopRightOnSquareIcon className="h-3 w-3 ml-1" />
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Info box for weekly digest */}
      {preferences.email_frequency === 'weekly_digest' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex">
            <InformationCircleIcon className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="ml-3">
              <h4 className="text-sm font-medium text-blue-900">Weekly Digest Enabled</h4>
              <p className="mt-1 text-sm text-blue-700">
                You'll receive a summary email every <strong>Monday at 8:00 AM UTC</strong> with:
              </p>
              <ul className="mt-2 text-sm text-blue-700 list-disc list-inside space-y-1">
                <li>New issues detected in the past week</li>
                <li>Issues resolved</li>
                <li>Top critical issues requiring attention</li>
                <li>Week-over-week trends</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Warning for disabled */}
      {preferences.email_frequency === 'disabled' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="ml-3">
              <h4 className="text-sm font-medium text-amber-900">Email Notifications Disabled</h4>
              <p className="mt-1 text-sm text-amber-700">
                You won't receive any email alerts. Make sure to check your dashboard regularly for data quality issues.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Advanced Options (only show for immediate) */}
      {preferences.email_frequency === 'immediate' && (
        <div className="bg-white border border-gray-200 rounded-lg">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center">
              <h4 className="text-base font-medium text-gray-900">Advanced Options</h4>
              <span className="ml-2 text-xs text-gray-500">Fine-tune what you receive</span>
            </div>
            {showAdvanced ? (
              <ChevronUpIcon className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDownIcon className="h-5 w-5 text-gray-400" />
            )}
          </button>

          {showAdvanced && (
            <div className="px-6 pb-6 space-y-6 border-t border-gray-200 pt-6">
              {/* Severity Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-3">
                  Minimum Severity Level
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Only notify me about issues at or above this severity
                </p>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="low"
                      checked={preferences.minimum_severity === 'low'}
                      onChange={(e) => setPreferences(prev => ({ ...prev, minimum_severity: e.target.value }))}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                    />
                    <span className="ml-3 text-sm">
                      <span className="font-medium text-gray-900">All issues</span>
                      <span className="text-gray-500"> - Low, Medium, and High severity</span>
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="medium"
                      checked={preferences.minimum_severity === 'medium'}
                      onChange={(e) => setPreferences(prev => ({ ...prev, minimum_severity: e.target.value }))}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                    />
                    <span className="ml-3 text-sm">
                      <span className="font-medium text-gray-900">Medium and High</span>
                      <span className="text-gray-500"> - Skip low severity (recommended)</span>
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="high"
                      checked={preferences.minimum_severity === 'high'}
                      onChange={(e) => setPreferences(prev => ({ ...prev, minimum_severity: e.target.value }))}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                    />
                    <span className="ml-3 text-sm">
                      <span className="font-medium text-gray-900">Only critical</span>
                      <span className="text-gray-500"> - High severity only</span>
                    </span>
                  </label>
                </div>
              </div>

              {/* Event Type Filters */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-3">
                  Event Types
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Choose which types of events trigger notifications
                </p>
                <div className="space-y-3">
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      checked={preferences.receive_validation_failures}
                      onChange={(e) => setPreferences(prev => ({ ...prev, receive_validation_failures: e.target.checked }))}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-0.5"
                    />
                    <span className="ml-3">
                      <span className="block text-sm font-medium text-gray-900">Validation Failures</span>
                      <span className="block text-xs text-gray-500">Data quality rules that fail</span>
                    </span>
                  </label>

                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      checked={preferences.receive_anomalies}
                      onChange={(e) => setPreferences(prev => ({ ...prev, receive_anomalies: e.target.checked }))}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-0.5"
                    />
                    <span className="ml-3">
                      <span className="block text-sm font-medium text-gray-900">Anomaly Detection</span>
                      <span className="block text-xs text-gray-500">Unusual patterns in your data (z-score, IQR, moving average)</span>
                    </span>
                  </label>

                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      checked={preferences.receive_custom_metric_breaches}
                      onChange={(e) => setPreferences(prev => ({ ...prev, receive_custom_metric_breaches: e.target.checked }))}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-0.5"
                    />
                    <span className="ml-3">
                      <span className="block text-sm font-medium text-gray-900">Custom Metric Breaches</span>
                      <span className="block text-xs text-gray-500">When custom metrics you've defined cross warning or critical thresholds</span>
                    </span>
                  </label>

                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      checked={preferences.receive_schema_changes}
                      onChange={(e) => setPreferences(prev => ({ ...prev, receive_schema_changes: e.target.checked }))}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-0.5"
                    />
                    <span className="ml-3">
                      <span className="block text-sm font-medium text-gray-900">Schema Changes</span>
                      <span className="block text-xs text-gray-500">Database structure modifications</span>
                    </span>
                  </label>

                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      checked={preferences.receive_automation_failures}
                      onChange={(e) => setPreferences(prev => ({ ...prev, receive_automation_failures: e.target.checked }))}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-0.5"
                    />
                    <span className="ml-3">
                      <span className="block text-sm font-medium text-gray-900">Automation Failures</span>
                      <span className="block text-xs text-gray-500">When scheduled jobs fail</span>
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Save Button */}
      <div className="flex justify-end pt-4 border-t border-gray-200">
        <button
          onClick={savePreferences}
          disabled={saving}
          className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </>
          ) : (
            <>
              <CheckIcon className="h-5 w-5 mr-2" />
              Save Preferences
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default UserPreferences;
